import React, { type ReactElement, useState } from 'react'
import { Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'

import { TrashIcon } from '~assets/icons'
import { Button } from '~components/common'
import ConfirmDeleteModal from '~components/common/ConfirmDeleteModal'

interface DeleteButtonProps<T> {
  item: T
  onDelete: (item: T) => void
  itemName?: string
  isDisabled?: boolean
  warningMessage?: string
  buttonSize?: 'small' | 'medium' | 'large'
  showLabel?: boolean
  modalTitle?: string
}

function DeleteButton<T>({
  item,
  onDelete,
  itemName,
  isDisabled = false,
  warningMessage,
  buttonSize = 'large',
  showLabel = false,
  modalTitle = 'Confirm Deletion',
}: DeleteButtonProps<T>): ReactElement {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const theme = useTheme()

  const handleDeleteClick = (e: React.MouseEvent): void => {
    e.stopPropagation()
    setIsModalOpen(true)
  }

  const handleCloseModal = (): void => {
    setIsModalOpen(false)
  }

  const handleConfirmDelete = (): void => {
    onDelete(item)
    setIsModalOpen(false)
  }

  return (
    <>
      <Button size={buttonSize} onClick={handleDeleteClick} disabled={isDisabled} variant='text'>
        <TrashIcon color={isDisabled ? theme.palette.grey[400] : theme.palette.text.primary} />
        {showLabel && (
          <Typography
            variant='body2'
            color={isDisabled ? theme.palette.grey[400] : theme.palette.text.primary}
          >
            {itemName ? `Delete ${itemName}` : 'Delete'}
          </Typography>
        )}
      </Button>

      <ConfirmDeleteModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDelete}
        title={modalTitle}
        description={warningMessage}
        itemName={itemName}
      />
    </>
  )
}

export default DeleteButton
